import { AddButton } from '@/components/Elements/Button'
import { camelToWords } from '@/utils/stringHelper'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Tag,
  TagLeftIcon,
  Text,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { IoAddOutline } from 'react-icons/io5'
import AppliedColumnCard, { formatDisplayLevelTag } from './AppliedColumnCard'
import { meta } from '@eslint/js'
import { getAllMyOfferDisplayGroupByLevel } from '@/features/offers/constants/offerList.constant'

const ColumnEditMenu = ({ columns, handleSaveEditColumn, modalShow = false, setModalShow, displayLevel = '' }) => {
  //Table State

  const nonActionColumns = columns

  //Local State
  const [availableColumns, setAvailableColumns] = useState([])
  const [appliedColumns, setAppliedColumns] = useState([])

  useEffect(() => {
    const visibleColumns = nonActionColumns.filter((col) => col.getIsVisible())
    const invisibleColumns = nonActionColumns.filter((col) => !col.getIsVisible())
    setAppliedColumns(visibleColumns)
    setAvailableColumns(invisibleColumns)
  }, [nonActionColumns])

  const availableColumnGroups = useMemo(() => {
    const groups = {}
    availableColumns.forEach((column) => {
      const displayLevel = column?.columnDef?.meta?.displayLevel ?? ''
      const displayColor = column?.columnDef?.meta?.displayColor ?? ''
      if (displayLevel !== '' && displayColor !== '' && !groups[displayLevel]) {
        groups[displayLevel] = {
          displayLevel: displayLevel,
          displayColor: displayColor,
        }
      }
    })
    return groups
  }, [availableColumns])

  const handleAddColumn = (column) => {
    setAppliedColumns((prev) => [...prev, column])
    setAvailableColumns((prev) => [...prev].filter((prevColumn) => prevColumn.id !== column.id))
  }
  const handleRemoveColumn = (column) => {
    setAppliedColumns((prev) => [...prev].filter((prevColumn) => prevColumn.id !== column.id))
    setAvailableColumns((prev) => [...prev, column])
  }

  const closeModal = () => {
    setModalShow(false)

    const visibleColumns = nonActionColumns.filter((col) => col.getIsVisible())
    const invisibleColumns = nonActionColumns.filter((col) => !col.getIsVisible())
    //Reset Unsaved changes
    setAppliedColumns(visibleColumns)
    setAvailableColumns(invisibleColumns)
  }
  const handleSave = () => {
    handleSaveEditColumn(appliedColumns, availableColumns)
    closeModal()
  }

  const handleAddAllColumns = () => {
    setAppliedColumns(nonActionColumns)
    setAvailableColumns([])
  }

  const handleRemoveAllColumns = () => {
    setAppliedColumns([])
    setAvailableColumns(nonActionColumns)
  }

  const handleAddColumnByGroup = (group) => {
    const selectedColumns = availableColumns.filter((col) => col?.columnDef?.meta?.displayLevel === group)

    setAvailableColumns((prev) => [...prev].filter((prevColumn) => prevColumn?.columnDef?.meta?.displayLevel !== group))

    setAppliedColumns((prev) => [...prev, ...selectedColumns])
  }

  const handleRemoveColumnByGroup = (group) => {
    const selectedColumns = appliedColumns.filter((col) => col?.columnDef?.meta?.displayLevel === group)

    setAppliedColumns((prev) => [...prev].filter((prevColumn) => prevColumn?.columnDef?.meta?.displayLevel !== group))
    setAvailableColumns((prev) => [...prev, ...selectedColumns])
  }

  const getTagColor = (metaDisplayLevel, metaDisplayColor) => {
    const displayGroups = getAllMyOfferDisplayGroupByLevel(displayLevel)

    if (displayGroups.includes(metaDisplayLevel)) {
      return metaDisplayColor
    }

    return metaDisplayColor
  }
  return (
    <>
      <Modal isOpen={modalShow} onClose={closeModal} size="xl">
        <ModalOverlay />
        <ModalContent mt="3" maxW="60%">
          <ModalHeader>Edit Columns</ModalHeader>
          <ModalCloseButton />
          <ModalBody backgroundColor={'#f7f9fb'}>
            {displayLevel !== '' && (
              <Flex bg="white" p="3" mb="2" borderRadius={10}>
                <Text>Display Level: {formatDisplayLevelTag(displayLevel)}</Text>
              </Flex>
            )}

            <Flex w="100%" justifyContent={'center'} gap="5" maxH="calc(100vh - 200px)" overflowY={'hidden'}>
              <Card w="50%" overflowY={'scroll'}>
                <CardHeader>
                  <Flex>
                    <Heading size="md">Available Columns</Heading>
                    <Spacer />
                    <Button colorScheme="teal" onClick={handleAddAllColumns}>
                      Add All
                    </Button>
                  </Flex>
                  <Flex gap="2">
                    {Object.values(availableColumnGroups).map((group) => (
                      <Tag
                        key={group.displayLevel}
                        size="md"
                        colorScheme={group.displayColor}
                        variant="solid"
                        _hover={{ cursor: 'pointer', color: 'blackAlpha.500' }}
                        onClick={() => handleAddColumnByGroup(group.displayLevel)}
                      >
                        <TagLeftIcon boxSize="12px" as={IoAddOutline} />
                        {formatDisplayLevelTag(group.displayLevel)}
                      </Tag>
                    ))}
                  </Flex>
                </CardHeader>
                <CardBody>
                  {availableColumns.map((column) => (
                    <Flex key={column.id} my="2" ml="5" gap="3">
                      <AddButton onClick={() => handleAddColumn(column)} />
                      {column?.columnDef?.meta?.displayLevel && (
                        <Tag size="sm" colorScheme={column?.columnDef?.meta?.displayColor} variant="solid">
                          {formatDisplayLevelTag(column?.columnDef?.meta?.displayLevel)}
                        </Tag>
                      )}

                      <Text>
                        {column?.columnDef?.header
                          ? column.columnDef.header({ header: { id: column.id } })
                          : camelToWords(column.id)}
                      </Text>
                    </Flex>
                  ))}
                </CardBody>
              </Card>
              <Divider orientation="vertical" />

              <AppliedColumnCard
                columns={appliedColumns}
                setColumns={setAppliedColumns}
                handleRemoveColumn={handleRemoveColumn}
                handleRemoveAllColumns={handleRemoveAllColumns}
                handleRemoveColumnByGroup={handleRemoveColumnByGroup}
                displayLevel={displayLevel}
              />
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Flex gap="2">
              <Button colorScheme="teal" onClick={handleSave}>
                Apply
              </Button>
              <Button colorScheme="gray" onClick={closeModal}>
                Close
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default ColumnEditMenu
